import React from 'react'

import type { SelectButtonProps } from './select.types'

import Button, { LineButton } from '../button'
import { useStyleConfig, vui } from '../core'
import { inputColors } from '../input/consts'
import { PopoverTrigger } from '../popover'
import { cs, ellipsisOverflow } from '../utils'
import { useSelectContext } from './context'

/**
 * Displays a custom button component, which opens a popover on click.
 * Text within the button is either selected value or placeholder.
 * Includes special styling for disabled, readOnly and invalid states.
 */
export const SelectButton = vui<'button', SelectButtonProps>((props, ref) => {
  const { className, ...rest } = props
  const { disabled, isInvalid, placeholder, readOnly, valueText } = useSelectContext() ?? {}
  const styles = useStyleConfig('Select', useSelectContext())

  const text = valueText || placeholder

  const stateProps = disabled
    ? {
        cursor: 'not-allowed',
        opacity: 0.5,
        disabledBg: 'var(--vui-color-sandstone-79)',
        disabledBorderColor: 'var(--vui-color-sandstone-79)',
        disabledColor: 'white',
      }
    : readOnly
      ? {
          cursor: 'auto',
          bg: 'var(--vui-color-sandstone-97)',
          borderColor: 'var(--vui-color-sandstone-55)',
          color: 'var(--vui-foreground-default)',
          hoverBg: 'var(--vui-color-sandstone-97)',
          hoverBorderColor: 'var(--vui-color-sandstone-55)',
        }
      : undefined

  const isInvalidProps = isInvalid
    ? {
        borderColor: 'energyRed.45',
        hoverBorderColor: 'energyRed.45',
      }
    : undefined

  const textColor = inputColors.inputText
  const iconColor = disabled || readOnly ? inputColors.inputText : 'seaBlue.main'

  return (
    <PopoverTrigger
      as={LineButton}
      className={cs('vui-selectButton', className)}
      aria-readonly={readOnly}
      disabled={disabled}
      focusRing={2}
      fontWeight="regular"
      endIcon={<Button.Icon color={iconColor} ml="auto" name="uiAngleDown" />}
      isFullWidth
      px={1}
      ref={ref}
      text={
        <Button.Text color={textColor} {...ellipsisOverflow}>
          {text}
        </Button.Text>
      }
      userSelect="text"
      {...styles.button}
      {...stateProps}
      {...isInvalidProps}
      {...rest}
    />
  )
})

SelectButton.displayName = 'SelectButton'
export default SelectButton
